import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { useWorkspaceNavOverlayScrimDismiss } from '~/components/v2/components/workspace/main-side-nav/useWorkspaceNavOverlayScrimDismiss';
import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';

/**
 * Page dimmer for the overlay rail. Must live on the workspace chrome (header +
 * content), not inside the overflow-hidden content row — `fixed`/`absolute`
 * there cannot cover the header, and the header's `z-40` info would also paint
 * above a `z-30` viewport scrim. `z-[45]` sits above that header layer and
 * below the rail (`z-50`) and notifications popover (`z-[200]`).
 * Opacity is driven by `group/main-side-nav-overlay` on that chrome — not the
 * rail's `group/main-side-nav`, which this scrim is not inside of.
 *
 * While an overlay slot is open, the scrim captures pointer events the same way
 * the notifications/uploads `PanelOverlay` does, so page content cannot hover
 * or activate underneath. `data-disableselect` also stops gallery marquee
 * (listening on `#__next`) from starting a drag on this layer. The rail stays
 * above this layer (`z-50`). Capture stays through pointerup so the dismissing
 * click does not fall through.
 */
export const WorkspaceNavOverlayScrim = memo(() => {
  const isPinned = useMainSideNavStore((state) => state.pinned);
  const { blocksPagePointerEvents, endOverlayDismiss, startOverlayDismiss } = useWorkspaceNavOverlayScrimDismiss();

  return (
    <div
      aria-hidden
      className={cn(
        'absolute inset-0 z-[45] bg-black/10 opacity-0 transition-opacity duration-200 dark:bg-black/40',
        blocksPagePointerEvents ? 'pointer-events-auto' : 'pointer-events-none',
        !isPinned &&
          'group-has-[[data-hover-expand]]/main-side-nav-overlay:opacity-100 group-has-[[data-main-side-nav]_[data-state=open]]/main-side-nav-overlay:opacity-100',
      )}
      data-disableselect="true"
      data-testid="MAIN_SIDE_NAV_SCRIM"
      onLostPointerCapture={blocksPagePointerEvents ? endOverlayDismiss : undefined}
      onPointerCancel={blocksPagePointerEvents ? endOverlayDismiss : undefined}
      onPointerDown={blocksPagePointerEvents ? startOverlayDismiss : undefined}
      onPointerUp={blocksPagePointerEvents ? endOverlayDismiss : undefined}
    />
  );
});

WorkspaceNavOverlayScrim.displayName = 'WorkspaceNavOverlayScrim';
